import React, { useMemo, useState, useEffect, useRef, memo } from 'react';
import { shape, func } from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import FundraiserSwitch from 'components/FundraiserSwitch';
import IconWithText from 'components/IconWithText';
import Image from 'components/Image';
import TxtpayForm from 'components/TxtpayForm';
import EmbedCode from 'components/EmbedCode';

import { ReactComponent as EditIcon } from 'assets/icons/pencil.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg';
import { ReactComponent as CodeIcon } from 'assets/icons/code.svg';
import { productList as TEXT } from 'locales/en';
import { showSuccessMessage } from 'redux/slices/notification';
import { priceForAPI } from 'utils/price';
import { logEvent } from 'utils/ga';

const TXTPAY = 'txtpay';
const EMBED_CODE = 'embedCode';

const FundraiserThumbnail = ({ product, onChangeOnSale }) => {
  const [expandedView, setExpandedView] = useState(null);
  const expandedRef = useRef(null);

  const history = useHistory();
  const dispatch = useDispatch();
  const {
    image,
    name,
    currency_symbol,
    id,
    on_sale,
    raised,
    goal,
    city,
    landing_page_url,
    checkout_url,
  } = product;

  const onCopyLink = () => {
    logEvent('click', {
      category: 'share',
      product_id: product.id,
    });
    try {
      navigator.clipboard.writeText(landing_page_url);
      dispatch(
        showSuccessMessage({
          message: 'Link copied to clipboard!',
          timeout: 2000,
        })
      );
    } catch {
      console.error('clipboard API not supported');
      window.open(landing_page_url);
    }
  };

  const raisedBarWidth = useMemo(() => {
    return `${(priceForAPI(raised) / priceForAPI(goal)) * 100}%`;
  }, [goal, raised]);

  const onClickItem = (item) => {
    if (item === expandedView) {
      setExpandedView(null);
    } else {
      setExpandedView(item);
    }
  };

  useEffect(() => {
    if (expandedView && expandedRef.current) {
      expandedRef.current.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [expandedView]);

  return (
    <div className="card shadow-lg p-0 w-auto">
      <div id="product-thumbnail" className="lg:grid lg:grid-cols-3">
        <div className="p-5 lg:col-span-2 lg:p-12">
          <div className="grid grid-cols-2 items-center justify-between">
            <div className="w-16 h-16 overflow-hidden rounded-md">
              <Image src={image} alt={name} />
            </div>
            <div className="justify-self-end flex">
              <span
                className="text-xs cursor-pointer hover:text-black items-center flex lg:flex-row text-gray-400 lg:mr-12 mr-6"
                onClick={() => {
                  logEvent('click', {
                    category: 'edit',
                    product_id: id,
                  });
                  history.push(`/fundraisers/${id}/edit`);
                }}
              >
                <EditIcon className="fill-current h-4 w-4" />
                <p className="ml-2 lg:mr-1">{TEXT.edit}</p>
              </span>
              <FundraiserSwitch value={on_sale} onChange={onChangeOnSale} />
            </div>
          </div>
          <h1
            data-testid="name"
            className="text-lg font-bold text-javelin-gray-dark truncate mt-5"
          >
            {name}
          </h1>
          <p className="text-sm text-javelin-gray-dark text-opacity-40 mt-2">
            {city || '-'}
          </p>
          <span
            data-testid="raised-amount"
            className="text-sm font-bold text-javelin-gray-dark mt-4 lg:mt-8 inline-block"
          >
            {`${currency_symbol}${raised} raised `}
            <span className="text-sm font-light text-javelin-placeholder">
              {`of ${currency_symbol}${goal} goal`}
            </span>
          </span>
          <div className="h-2 w-full bg-gray-100 mt-5 overflow-hidden">
            <div
              style={{ width: raisedBarWidth }}
              className="bg-brand-primary h-full"
            ></div>
          </div>
        </div>
        <div className="bg-gray-50 bg-opacity-50 px-5 items-start grid grid-flow-row grid-rows-3">
          <div className="h-full border-b border-gray-200 flex overflow-hidden justify-between items-center">
            <IconWithText
              icon={LinkIcon}
              text={TEXT.getLink}
              className="h-14 w-9/12 lg:w-10/12"
              testId="get-link"
            />
            <span
              data-testid="copy-link"
              onClick={onCopyLink}
              className="text-sm cursor-pointer rounded-full self-center justify-self-end px-4 py-2 bg-brand-primary hover:opacity-90 text-white select-none"
            >
              {TEXT.copy}
            </span>
          </div>
          <IconWithText
            icon={CodeIcon}
            text={TEXT.getCode}
            active={expandedView === EMBED_CODE}
            className="border-b border-gray-200 h-full w-full cursor-pointer  hover:text-brand-primary transition-colors duration-300"
            testId="get-embed-code"
            onClick={() => onClickItem(EMBED_CODE)}
          />
          <IconWithText
            icon={LinkIcon}
            text={TEXT.txtpayShare}
            active={expandedView === TXTPAY}
            className="h-full w-full cursor-pointer hover:text-brand-primary transition-colors duration-300"
            testId="txtpay-share"
            onClick={() => onClickItem(TXTPAY)}
          />
        </div>
      </div>
      {expandedView === TXTPAY && (
        <TxtpayForm
          ref={expandedRef}
          id={id}
          fundraiserName={name}
          checkoutUrl={checkout_url}
        />
      )}
      {expandedView === EMBED_CODE && <EmbedCode ref={expandedRef} id={id} />}
    </div>
  );
};

FundraiserThumbnail.propTypes = {
  product: shape({}),
  onChangeOnSale: func,
};
export default memo(FundraiserThumbnail);
